/**
 * AIPanel — modal for Claude-assisted channel generation (Mode C / counterpoint).
 *
 * Workflow: pick target channel → tweak key/instruction → Generate → preview →
 * Apply (overwrites that channel in the current pattern) / Try again / Close.
 *
 * The "existing voices" payload contains only the other channels in the
 * current pattern. The AI sees the cell-event JSON, not the full song.
 */

import { useEffect, useMemo, useState } from 'react';
import type { DemoSong } from '../audio/demo-song';
import {
  AIClientError,
  generateCounterpoint,
  type CounterpointResult,
} from '../lib/ai-client';
import {
  patternToExistingChannels,
  type ChannelEvent,
  type EventChannel,
} from '../lib/channel-events';

interface Props {
  open: boolean;
  onClose: () => void;
  song: DemoSong;
  patternIndex: number;
  tempo: number;
  onApply: (channel: EventChannel, events: ChannelEvent[]) => void;
}

interface ChannelOption {
  value: EventChannel;
  label: string;
  hint: string;
  colorClass: string;
}

const CHANNELS: ChannelOption[] = [
  { value: 'pulse1', label: 'P1', hint: 'Pulse 1 — lead', colorClass: 'border-nes-cyan/40 text-nes-cyan' },
  { value: 'pulse2', label: 'P2', hint: 'Pulse 2 — counter', colorClass: 'border-nes-cyan/40 text-nes-cyan' },
  { value: 'triangle', label: 'TRI', hint: 'Triangle — bass', colorClass: 'border-nes-white/40 text-nes-white' },
  { value: 'noise', label: 'NSE', hint: 'Noise — percussion', colorClass: 'border-nes-red/40 text-nes-red' },
];

const INSTRUCTION_PRESETS: Record<EventChannel, string> = {
  pulse1: 'Memorable lead melody in the MM2/3 style. Dense 16th-note motion with chromatic passing tones, occasional sixths/octave leaps, and a 16th-note flourish at the end of the phrase.',
  pulse2: 'Independent counter-melody. Contrary motion when pulse 1 ascends. Hold long notes when pulse 1 is dense; move when pulse 1 holds. Avoid parallel fifths/octaves.',
  triangle: 'Walking bass through chord tones. 8th-note motion, octave jumps on beats 1 and 3, chromatic approach tones one 8th before chord changes.',
  noise: 'Rock drum pattern: noise hi-hat hits on every 16th (use "on" as the note marker). Use "OFF" on rows where you want a gap (e.g. just before a snare hit).',
};

export function AIPanel({ open, onClose, song, patternIndex, tempo, onApply }: Props) {
  const [targetChannel, setTargetChannel] = useState<EventChannel>('pulse2');
  const [key, setKey] = useState('Am');
  const [instruction, setInstruction] = useState(INSTRUCTION_PRESETS.pulse2);
  const [touchedInstruction, setTouchedInstruction] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CounterpointResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const targetPattern = song.patterns[patternIndex] ?? [];
  const length = song.rowsPerPattern;
  const existingChannels = useMemo(
    () => patternToExistingChannels(targetPattern, targetChannel),
    [targetPattern, targetChannel],
  );
  const existingChannelNames = Object.keys(existingChannels);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  // Re-fill instruction when channel changes (unless user has typed their own).
  useEffect(() => {
    if (!touchedInstruction) {
      setInstruction(INSTRUCTION_PRESETS[targetChannel]);
    }
  }, [targetChannel, touchedInstruction]);

  async function generate() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await generateCounterpoint({
        key,
        tempo,
        length,
        existing_channels: existingChannels,
        target_channel: targetChannel,
        instruction,
      });
      setResult(res);
    } catch (e: unknown) {
      if (e instanceof AIClientError) {
        setError(`${e.message} (HTTP ${e.status})`);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }

  function apply() {
    if (!result) return;
    onApply(result.channel, result.events);
    onClose();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded border border-nes-cyan/40 bg-nes-dim p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-nes-cyan">🤖 AI · generate channel</h3>
          <button
            onClick={onClose}
            className="rounded border border-nes-white/30 px-2 py-1 text-xs text-nes-white/70 hover:bg-nes-white/5"
          >
            Close
          </button>
        </header>

        <fieldset>
          <legend className="mb-1 text-[10px] uppercase tracking-wider text-nes-white/40">
            Target channel (will be overwritten on Apply)
          </legend>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {CHANNELS.map((c) => {
              const active = c.value === targetChannel;
              return (
                <button
                  key={c.value}
                  onClick={() => setTargetChannel(c.value)}
                  disabled={loading}
                  className={`flex flex-col items-start gap-1 rounded border p-2 text-left text-xs transition ${
                    active
                      ? 'border-nes-cyan bg-nes-cyan/15 text-nes-cyan'
                      : `${c.colorClass} hover:bg-nes-white/5`
                  } ${loading ? 'opacity-50' : ''}`}
                >
                  <span className="font-bold tracking-wider">{c.label}</span>
                  <span className="text-[10px] opacity-80">{c.hint}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-nes-white/70">Key</span>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              disabled={loading}
              placeholder="Am, Em, C, …"
              className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-white"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-nes-white/70">Tempo (from song)</span>
            <input
              type="text"
              value={`${tempo} BPM`}
              disabled
              className="rounded border border-nes-cyan/20 bg-nes-black/60 px-2 py-1 text-nes-white/60"
            />
          </label>
        </div>

        <label className="flex flex-col gap-1 text-xs">
          <span className="text-nes-white/70">
            Instruction
            <span className="ml-2 text-[10px] text-nes-white/40">
              (preset for {targetChannel} — edit freely)
            </span>
          </span>
          <textarea
            value={instruction}
            onChange={(e) => {
              setInstruction(e.target.value);
              setTouchedInstruction(true);
            }}
            disabled={loading}
            rows={4}
            className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-white"
          />
        </label>

        <details className="rounded border border-nes-cyan/20 bg-nes-black/40 p-2 text-xs">
          <summary className="cursor-pointer text-nes-cyan/80">
            Context sent to AI ({existingChannelNames.length}{' '}
            {existingChannelNames.length === 1 ? 'channel' : 'channels'} from pattern {patternIndex})
          </summary>
          {existingChannelNames.length === 0 ? (
            <p className="mt-2 text-[10px] text-nes-white/40">
              No other channels populated — the AI will compose this voice from scratch in the
              given key.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 text-[10px] text-nes-white/60">
              {existingChannelNames.map((ch) => (
                <li key={ch}>
                  <span className="font-bold text-nes-white/80">{ch}</span>:{' '}
                  {existingChannels[ch]!.length} events
                </li>
              ))}
            </ul>
          )}
        </details>

        {error && (
          <div className="rounded border border-nes-red/40 bg-nes-red/10 p-2 text-xs text-nes-red">
            ⚠ {error}
          </div>
        )}

        {result && (
          <div className="rounded border border-nes-cyan/40 bg-nes-black p-3 text-xs">
            <p className="font-bold text-nes-cyan">
              ✓ {result.events.length} events for {result.channel}
            </p>
            <p className="mt-1 text-[10px] text-nes-white/40">
              {result.meta.model} · {result.meta.tokens_in} in + {result.meta.tokens_out} out
              tokens
              {result.meta.cached ? ' · cache hit' : ''}
            </p>
            <p className="mt-2 text-[10px] text-nes-white/40">
              First few notes:{' '}
              {result.events
                .slice(0, 8)
                .map((e) => `${e.step}=${e.note ?? 'OFF'}`)
                .join(', ')}
              {result.events.length > 8 ? '…' : ''}
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          {result ? (
            <>
              <button
                onClick={() => setResult(null)}
                className="rounded border border-nes-white/30 px-3 py-1 text-xs hover:bg-nes-white/5"
              >
                Try again
              </button>
              <button
                onClick={apply}
                className="rounded bg-nes-cyan px-3 py-1 text-xs font-bold text-nes-black hover:bg-nes-cyan/80"
              >
                Apply to pattern {patternIndex}
              </button>
            </>
          ) : (
            <button
              onClick={generate}
              disabled={loading}
              className="rounded bg-nes-cyan px-4 py-2 text-xs font-bold text-nes-black hover:bg-nes-cyan/80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? '⏳ generating…' : '🤖 Generate'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
